import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { repairsBySerialNumbersQuery, unitStructureQuery } from 'api/queries';
import { AreaName } from 'domain/constants';
import { getAllDrivelinesSorted } from 'domain/unit/Unit';
import { useDialogState } from 'library/hooks/useDialogState';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { reactPlugin } from 'main/azureInsights/config';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { RepairsDataGrid } from 'pages/productindividualview/sectionPages/Services/RepairsDataGrid';
import { SelectRepairSerialNumberDialog } from 'pages/productindividualview/sectionPages/Services/SelectRepairSerialNumberDialog';

const UnitRepairsSectionPage = (): React.JSX.Element => {
	const { t } = useTranslation(['commonButton']);
	const unitIds = useResolvedUnitIds();
	const { api } = useProductCenterApi();
	const { data: unit } = useSuspenseQuery(unitStructureQuery(unitIds.id));
	const { open, handleOpen, handleClose } = useDialogState();

	const productIndividuals = useMemo(
		() =>
			getAllDrivelinesSorted(unit).flatMap(
				({ productIndividualSubSystems }) =>
					productIndividualSubSystems.flatMap(
						({ productIndividuals }) => productIndividuals
					)
			),
		[unit]
	);

	const serialNumbers = useMemo(
		() => productIndividuals.map(({ serialNumber }) => serialNumber),
		[productIndividuals]
	);

	const { data } = useSuspenseQuery(
		repairsBySerialNumbersQuery(api, serialNumbers)
	);

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
				}}>
				<AuthorizeComponent
					areaName={AreaName.Repair}
					permission={OperationType.Create}>
					<Button
						startIcon={<Add />}
						variant="outlined"
						onClick={handleOpen}>
						{t('commonButton:add')}
					</Button>
				</AuthorizeComponent>
			</Box>
			<RepairsDataGrid data={data} showSerialNumber />
			<SelectRepairSerialNumberDialog
				open={open}
				onClose={handleClose}
				productIndividuals={productIndividuals}
			/>
		</>
	);
};

export default withAITracking(
	reactPlugin,
	UnitRepairsSectionPage,
	'UnitOverviewRepairsSectionPage',
	'application-insight-container'
);
